// Content configuration for VulnReach website
// Update these values to reflect your actual repository and package information

export const badges = ['OWASP Project', 'Apache 2.0', 'Python · Java · JavaScript', 'SCA + Taint Analysis', 'Runtime Reachability', 'eBPF Observer (experimental)']

export const whyCards = [
  {
    title: 'The Problem',
    body: 'Most scanners report presence. Attackers exploit reachability and execution paths.',
  },
  {
    title: 'The Gap',
    body: 'SBOMs and SCA lack runtime and call-graph context. You need execution-aware evidence.',
  },
  {
    title: 'The Fix',
    body: 'VulnReach classifies every CVE through a five-layer evidence chain and produces a Runtime Bill of Materials (RBOM), so teams fix what is truly reachable and suppress what is not.',
  },
]

export const flow = [
  'SCA (Trivy)',
  'Taint Analysis',
  'Call Graph (AST)',
  'Route Exposure',
  'Runtime Coverage',
  'RBOM + Verdicts',
]

export const capabilities = [
  'SCA via Trivy (CVE ingestion)',
  'Taint tracking — user input → vulnerable sinks (Python, Java, JavaScript)',
  'Call-graph reachability — Python production-ready; Java & JavaScript experimental',
  'Transitive reachability from lockfiles (poetry.lock, uv.lock, package-lock.json)',
  'Runtime confirmation via Docker-based coverage collection',
  'eBPF runtime observer — Linux, explicit opt-in (experimental)',
  'Runtime Bill of Materials (RBOM) + CycloneDX export',
  'CI/CD gating with policy.block_if — trigger and gate via curl',
  'Web UI with fix plans, call-graph replay, and PDF export',
  'JWT auth, role-based access, and long-lived API keys',
  'On-demand AI next-steps per finding — the verdict stays deterministic',
  'No vendor lock-in — LLM features default off; Ollama for offline use',
]

export const philosophy = [
  'No black-box scoring',
  'No SaaS lock-in',
  'Built for engineers',
  'OSS-first security',
]

// 7 days from today (2026-03-07)
export const launchDate = new Date('2026-03-14T00:00:00Z')

export const openSource = {
  license: 'Apache 2.0',
}

export const maintainer = {
  name: 'Hrishikesh N',
  site: 'https://ihrishikesh0896.github.io/procrastinator/',
  linkedin: 'https://www.linkedin.com/in/hrishikesh-n/',
  github: 'https://github.com/ihrishikesh0896',
}

export const cliSnippet = `# 1. Start the server (API + web UI on :8000)
git clone https://github.com/OWASP/VulnReach.git && cd VulnReach
cp .env.example .env.local      # replace every CHANGE_ME (openssl rand -hex 32)
docker compose up -d --build    # or: pip install vulnreach && vulnreach start

# 2. Authenticate (or use an API key from Settings → API Keys)
TOKEN=$(curl -s -X POST http://localhost:8000/login \
  -H "Content-Type: application/json" \
  -d '{"username":"<admin-user>","password":"<admin-password>"}' | jq -r .access_token)

# 3. Start a scan
curl -X POST http://localhost:8000/scan \
  -H "Authorization: Bearer $TOKEN" \
  -H "Content-Type: application/json" \
  -d '{"repo_url":"https://github.com/yourorg/yourapp"}'

# 4. Poll for results — or open http://localhost:8000 in your browser
curl http://localhost:8000/scan/<scan_id> \
  -H "Authorization: Bearer $TOKEN" | jq .summary`

export const configSnippet = `scan:
  static_reachability: true
  tools:
    - trivy                        # SCA (required)
    - tainter                      # taint analysis (optional)
    - multi_language_reachability  # monorepo-aware static reachability
    - route_extractor              # HTTP route map
    - metadata                     # PyPI → import name map
    - dynamic_reachability         # Docker-based runtime coverage
  runtime:
    enabled: true                  # requires VULNREACH_ALLOW_DOCKER_DAEMON=true
    timeout: 120
    container_port: 3000
    ebpf:
      enabled: false               # experimental — Linux only

policy:
  block_if:
    - severity: CRITICAL
      verdict: CONFIRMED
    - severity: HIGH
      verdict: CONFIRMED`

export const demo = {
  videoUrl: 'https://youtu.be/d0lCo1nJzVM',
  poster: '/screenshots/demo-video.webp',
  screenshots: [
    {
      src: '/screenshots/sca-only.webp',
      width: 1600,
      height: 536,
      fit: 'contain',
      step: 'Step 1',
      title: 'SCA alone: 74 CVEs',
      caption: 'Trivy reports every vulnerable package, with no sense of which ones matter.',
    },
    {
      src: '/screenshots/fix-plan.webp',
      width: 1388,
      height: 1472,
      step: 'Step 2',
      title: 'Fix plan: 7 packages, 50 confirmed reachable',
      caption: 'VulnReach proves which CVEs are reachable and maps each to an upgrade target.',
    },
    {
      src: '/screenshots/findings.webp',
      width: 1384,
      height: 1462,
      step: 'Findings',
      title: 'An evidence chain per CVE',
      caption: 'Each finding shows the taint path, call chain, route, and runtime evidence behind its verdict.',
    },
    {
      src: '/screenshots/dashboard.webp',
      width: 1600,
      height: 823,
      step: 'Dashboard',
      title: '42 confirmed reachable across 9 repos',
      caption: 'Track reachable risk across every repository you scan.',
    },
  ],
}

// Navigation items - these map to section IDs
export const navigation = [
  { label: 'Why', href: '#why' },
  { label: 'How', href: '#how' },
  { label: 'Demo', href: '#demo' },
  { label: 'Capabilities', href: '#capabilities' },
  { label: 'Snippet', href: '#snippet' },
  { label: 'Open Source', href: '#open-source' },
]
